import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export type BadgeVariant = 'neutral' | 'positive' | 'caution' | 'danger' | 'info';

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  icon?: LucideIcon;
  className?: string;
}

const VARIANT: Record<BadgeVariant, string> = {
  neutral: 'bg-paper-sunk text-ink-soft',
  positive: 'bg-positive text-paper',
  caution: 'bg-caution text-paper',
  danger: 'bg-danger text-paper',
  info: 'bg-accent text-accent-fg',
};

/** Auto-icon fallbacks so status color is never the only carrier of meaning. */
const DEFAULT_ICON: Record<BadgeVariant, LucideIcon | null> = {
  neutral: null,
  positive: CheckCircle2,
  caution: AlertTriangle,
  danger: AlertCircle,
  info: Info,
};

export function Badge({ variant = 'neutral', icon, children, className }: BadgeProps): JSX.Element {
  const Icon = icon ?? DEFAULT_ICON[variant];
  return (
    <span
      className={cn(
        'inline-flex h-5 items-center gap-1 rounded-xs px-1.5 text-[11px] font-medium leading-none',
        VARIANT[variant],
        className,
      )}
    >
      {Icon ? <Icon aria-hidden className="size-3" strokeWidth={2.5} /> : null}
      {children}
    </span>
  );
}
